import * as PIXI from 'pixi.js';
import type { ICollisionObject } from '@/types/CollisionTypes';

export interface ColliderOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  color?: number;
  visible?: boolean;
}

export class Collider implements ICollisionObject {
  public sprite: PIXI.Graphics;
  public bounds: PIXI.Rectangle;
  private color: number;
  private isVisible: boolean;

  constructor(options: ColliderOptions) {
    const { x, y, width, height, color = 0xff0000, visible = true } = options;

    this.bounds = new PIXI.Rectangle(x, y, width, height);
    this.color = color;
    this.isVisible = visible;

    // Create visual representation of the collider
    this.sprite = new PIXI.Graphics();
    this.sprite.x = x;
    this.sprite.y = y;
    
    // Draw initial state
    this.redraw();
  }

  private redraw(): void {
    this.sprite.clear();
    
    if (this.isVisible) {
      this.sprite.beginFill(this.color, 0.3); // Semi-transparent
      this.sprite.lineStyle(2, this.color, 0.8);
      this.sprite.drawRect(0, 0, this.bounds.width, this.bounds.height);
      this.sprite.endFill();
    }
  }

  /**
   * Check if a point is inside this collider
   */
  public containsPoint(x: number, y: number): boolean {
    return this.bounds.contains(x, y);
  }

  /**
   * Check if a rectangle intersects with this collider
   */
  public intersects(rect: PIXI.Rectangle): boolean {
    return this.bounds.intersects(rect);
  }

  /**
   * Check if another collider intersects with this one
   */
  public intersectsCollider(other: Collider): boolean {
    return this.bounds.intersects(other.bounds);
  }

  /**
   * Get the bounds of this collider
   */
  public getBounds(): PIXI.Rectangle {
    return this.bounds.clone();
  }

  /**
   * Update the collider position
   */
  public setPosition(x: number, y: number): void {
    this.bounds.x = x;
    this.bounds.y = y;
    this.sprite.x = x;
    this.sprite.y = y;
  }

  /**
   * Set visibility of the collider
   */
  public setVisible(visible: boolean): void {
    if (this.isVisible !== visible) {
      this.isVisible = visible;
      this.redraw();
    }
  }
}
